import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Mountain, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Box,
  Hammer,
  Gem,
  Shapes,
  Home,
  Building,
  Gift,
  User
} from 'lucide-react';
import { toast } from 'sonner';

// Sculpture types
const SCULPTURE_TYPES = [
  { value: 'busto', label: 'Busto / Ritratto', icon: User },
  { value: 'figura_intera', label: 'Figura intera', icon: User },
  { value: 'astratto', label: 'Scultura astratta', icon: Shapes },
  { value: 'animale', label: 'Animale / Pet', icon: Heart },
  { value: 'bassorilievo', label: 'Bassorilievo / Altorilievo', icon: Box },
  { value: 'decorativa', label: 'Scultura decorativa', icon: Sparkles },
  { value: 'monumentale', label: 'Monumentale / Grande formato', icon: Mountain },
  { value: 'miniatura', label: 'Miniatura / Da collezione', icon: Gem },
  { value: 'fontana', label: 'Fontana / Elemento acqua', icon: Box },
  { value: 'sacra', label: 'Arte sacra / Religiosa', icon: Star },
  { value: 'riproduzione', label: 'Riproduzione opera classica', icon: Box },
  { value: 'altro', label: 'Altro', icon: Mountain },
];

// Material preferences
const MATERIALS = [
  { value: 'marmo', label: 'Marmo', icon: Mountain },
  { value: 'bronzo', label: 'Bronzo', icon: Gem },
  { value: 'terracotta', label: 'Terracotta / Argilla', icon: Box },
  { value: 'legno', label: 'Legno', icon: Box },
  { value: 'pietra', label: 'Pietra (travertino, arenaria...)', icon: Mountain },
  { value: 'gesso', label: 'Gesso', icon: Box },
  { value: 'resina', label: 'Resina', icon: Shapes },
  { value: 'ceramica', label: 'Ceramica', icon: Box },
  { value: 'metallo', label: 'Metallo (ferro, acciaio, corten)', icon: Hammer },
  { value: 'vetro', label: 'Vetro', icon: Gem },
  { value: 'misto', label: 'Materiali misti', icon: Shapes },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Purpose/Destination
const SCULPTURE_PURPOSES = [
  { value: 'interno_casa', label: 'Per interni - Casa', icon: Home },
  { value: 'esterno_giardino', label: 'Per esterni - Giardino', icon: Mountain },
  { value: 'ufficio', label: 'Per ufficio / Studio', icon: Building },
  { value: 'regalo', label: 'Come regalo', icon: Gift },
  { value: 'collezione', label: 'Per la mia collezione', icon: Gem },
  { value: 'commemorativo', label: 'Commemorativo / Memoriale', icon: Heart },
  { value: 'pubblico', label: 'Spazio pubblico / Piazza', icon: Building },
  { value: 'azienda', label: 'Per azienda / Corporate', icon: Building },
  { value: 'altro', label: 'Altro', icon: Mountain },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'miniatura', label: 'Miniatura (fino a 20 cm)', description: 'Da scrivania' },
  { value: 'piccola', label: 'Piccola (20-50 cm)', description: 'Da mensola' },
  { value: 'media', label: 'Media (50-100 cm)', description: 'Da piedistallo' },
  { value: 'grande', label: 'Grande (100-200 cm)', description: 'Da pavimento' },
  { value: 'monumentale', label: 'Monumentale (200+ cm)', description: 'Installazione' },
  { value: 'non_so', label: 'Non so ancora', description: 'Vorrei consiglio' },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '200-500', label: '€200 - €500', description: 'Piccole sculture' },
  { value: '500-1000', label: '€500 - €1.000', description: 'Sculture medie' },
  { value: '1000-3000', label: '€1.000 - €3.000', description: 'Sculture importanti' },
  { value: '3000-5000', label: '€3.000 - €5.000', description: 'Commissioni elaborate' },
  { value: '5000-10000', label: '€5.000 - €10.000', description: 'Grandi formati / Bronzo' },
  { value: '10000+', label: '€10.000+', description: 'Monumentali' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1month', label: '1 Mese', description: 'Urgente' },
  { value: '2months', label: '2 Mesi', description: 'Prioritario' },
  { value: '3months', label: '3 Mesi', description: 'Standard' },
  { value: '6months', label: '6 Mesi', description: 'Progetti complessi' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example sculptures
const SCULPTURE_EXAMPLES = [
  {
    idea: "Busto in marmo dei miei genitori",
    result: "Doppio busto in marmo bianco di Carrara 45cm, dettagli realistici",
    image: "https://images.unsplash.com/photo-1615497994569-db84b0f1dd55?w=400&h=300&fit=crop",
    category: "Busti"
  },
  {
    idea: "Scultura astratta per il giardino",
    result: "Opera in acciaio corten 180cm, forme fluide ispirate al vento",
    image: "https://images.pexels.com/photos/11843706/pexels-photo-11843706.jpeg?w=400&h=300&fit=crop",
    category: "Astratto"
  },
  {
    idea: "Scultura del mio cavallo per il salotto",
    result: "Scultura in bronzo patinato 40cm, posa dinamica e dettagli anatomici",
    image: "https://images.unsplash.com/photo-1651085410796-e663860b2b08?w=400&h=300&fit=crop",
    category: "Animali"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho commissionato un busto in marmo di mio nonno da una vecchia foto. L'artista ha fatto un lavoro incredibile, sembra vivo.",
    author: "Roberto M.",
    location: "Carrara"
  },
  {
    text: "Cercavo una scultura per il giardino. Lo scultore ha creato un'opera in bronzo che valorizza tutto lo spazio esterno.",
    author: "Elena e Marco",
    location: "Firenze"
  },
  {
    text: "Una scultura in legno del mio gatto. La qualità del dettaglio e la posa sono perfette. Un ricordo eterno.",
    author: "Giulia S.",
    location: "Bologna"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "È gratuito inviare una richiesta?",
    answer: "Sì, è completamente gratuito. Descrivi la scultura che desideri e ricevi proposte da scultori senza alcun costo."
  },
  {
    question: "Sono obbligato ad acquistare?",
    answer: "Assolutamente no. Ricevi le proposte, le valuti con calma e decidi solo se trovi lo scultore e lo stile perfetto per te."
  },
  {
    question: "Posso vedere bozzetti prima dell'opera finale?",
    answer: "Certamente! Molti scultori realizzano bozzetti in argilla o modelli 3D per approvazione prima di procedere con l'opera definitiva."
  },
  {
    question: "Quanto tempo ci vuole per realizzare una scultura?",
    answer: "Dipende dalla complessità e dal materiale. Sculture piccole richiedono 2-4 settimane, opere in bronzo con fusione possono richiedere 3-6 mesi."
  },
  {
    question: "Quali materiali sono adatti per esterni?",
    answer: "Bronzo, pietra, marmo, acciaio corten e alcune resine sono ideali per esterni. Lo scultore ti consiglierà in base al clima e alla posizione."
  }
];

export default function SculturaRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    sculpture_type: '',
    material: '',
    purpose: '',
    size: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Sculture su Commissione | Scultori Italiani | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `sculpture-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi la scultura che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_sculpture_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const sculptureTypeLabel = SCULPTURE_TYPES.find(t => t.value === formData.sculpture_type)?.label || '';
      const materialLabel = MATERIALS.find(m => m.value === formData.material)?.label || '';
      const purposeLabel = SCULPTURE_PURPOSES.find(p => p.value === formData.purpose)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(s => s.value === formData.size)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (sculptureTypeLabel) details.push(`🗿  Tipo scultura: ${sculptureTypeLabel}`);
      if (materialLabel) details.push(`⚒️  Materiale: ${materialLabel}`);
      if (purposeLabel) details.push(`🏛️  Destinazione: ${purposeLabel}`);
      if (sizeLabel) details.push(`📐  Dimensioni: ${sizeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${sculptureTypeLabel} - ${materialLabel}`.trim() || 'Scultura su commissione',
        description: fullDescription,
        category: 'Scultura',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli scultori.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('sculpture-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Sculture su Commissione | Busti e Statue da Scultori Italiani | Rarifex</title>
        <meta name="description" content="Commissiona una scultura unica da scultori italiani. Busti, statue, opere in marmo, bronzo, legno. Descrivi la tua idea e ricevi proposte personalizzate. Gratuito e senza impegno." />
        <meta name="keywords" content="scultura su commissione, busto personalizzato, statua su commissione, scultore italiano, marmo carrara, bronzo, scultura su misura, arte scultorea" />
        <link rel="canonical" href="https://www.rarifex.com/sculture-su-commissione" />
        <meta property="og:title" content="Sculture su Commissione | Scultori Italiani | Rarifex" />
        <meta property="og:description" content="Commissiona una scultura unica da scultori italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/sculture-su-commissione" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50/50 via-white to-amber-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-stone-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-stone-100 text-stone-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Mountain className="w-4 h-4" />
                    Sculture su Commissione
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    La scultura{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-stone-600 to-amber-600">
                      che hai sempre sognato
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Scultori italiani la creano apposta per te.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Descrivi l'opera che immagini e ricevi proposte personalizzate da scultori e artisti esperti.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Scultori professionisti</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-stone-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="sculpture-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Busto commemorativo in marmo bianco"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="sculpture-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi la scultura che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Soggetto, stile, materiale preferito, dimensioni approssimative. Se hai foto di riferimento (per busti/ritratti), potrai allegarle sotto."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="sculpture-description-textarea"
                        />
                      </div>
                      
                      {/* Sculpture Type & Material */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Tipo di scultura
                          </Label>
                          <select
                            value={formData.sculpture_type}
                            onChange={(e) => handleInputChange('sculpture_type', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="sculpture-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {SCULPTURE_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Materiale preferito
                          </Label>
                          <select
                            value={formData.material}
                            onChange={(e) => handleInputChange('material', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="sculpture-material-select"
                          >
                            <option value="">Seleziona...</option>
                            {MATERIALS.map(mat => (
                              <option key={mat.value} value={mat.value}>{mat.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Purpose & Size */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Destinazione</Label>
                          <select
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="sculpture-purpose-select"
                          >
                            <option value="">Seleziona...</option>
                            {SCULPTURE_PURPOSES.map(purpose => (
                              <option key={purpose.value} value={purpose.value}>{purpose.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Dimensioni</Label>
                          <select
                            value={formData.size}
                            onChange={(e) => handleInputChange('size', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="sculpture-size-select"
                          >
                            <option value="">Seleziona...</option>
                            {SIZE_OPTIONS.map(size => (
                              <option key={size.value} value={size.value}>{size.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Budget & Timeline */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="sculpture-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(budget => (
                              <option key={budget.value} value={budget.value}>{budget.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tempistiche</Label>
                          <select
                            value={formData.timeline}
                            onChange={(e) => handleInputChange('timeline', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="sculpture-timeline-select"
                          >
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(timeline => (
                              <option key={timeline.value} value={timeline.value}>{timeline.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Image upload */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di riferimento (opzionale)
                        </Label>
                        <div className="mt-1.5">
                          <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-stone-400 hover:bg-stone-50/50 transition-colors">
                            {uploadingFile ? (
                              <Loader2 className="w-5 h-5 animate-spin text-stone-500" />
                            ) : (
                              <Upload className="w-5 h-5 text-slate-400" />
                            )}
                            <span className="text-sm text-slate-500">
                              {uploadingFile ? 'Caricamento...' : 'Carica foto, bozzetti o ispirazioni'}
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleFileUpload}
                              className="hidden"
                              disabled={uploadingFile}
                            />
                          </label>
                        </div>
                        
                        {/* Uploaded images */}
                        {formData.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.attachments.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Riferimento ${index + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg border"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Submit button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-stone-600 to-amber-600 hover:from-stone-700 hover:to-amber-700 text-white py-6 text-lg font-semibold shadow-lg"
                        data-testid="sculpture-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            Richiedi Preventivi Gratuiti
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-center text-slate-400">
                        Nessun impegno. Riceverai proposte da scultori italiani.
                      </p>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Come Funziona
              </h2>
              <p className="text-lg text-slate-600">
                Dalla tua idea alla scultura finita in 4 semplici passi
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-4 gap-8">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Raccontaci la scultura che immagini', color: 'bg-stone-100 text-stone-600' },
                { icon: Users, title: 'Ricevi', desc: 'Scultori inviano proposte e bozzetti', color: 'bg-amber-100 text-amber-600' },
                { icon: CheckCircle, title: 'Scegli', desc: 'Seleziona lo scultore ideale', color: 'bg-emerald-100 text-emerald-600' },
                { icon: Star, title: 'Ammira', desc: 'Ricevi la tua opera unica', color: 'bg-stone-100 text-stone-600' },
              ].map((step, i) => (
                <div key={i} className="text-center">
                  <div className={`w-16 h-16 ${step.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <step.icon className="w-8 h-8" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Esempi di Sculture Realizzate
              </h2>
              <p className="text-lg text-slate-600">
                Lasciati ispirare da alcune commissioni completate dai nostri scultori
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
              {SCULPTURE_EXAMPLES.map((example, i) => (
                <Card key={i} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-[4/3] relative">
                    <img src={example.image} alt={example.idea} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3">
                      <span className="bg-white/90 backdrop-blur-sm text-stone-700 text-xs font-medium px-3 py-1 rounded-full">
                        {example.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-sm text-slate-500 mb-1">Richiesta:</p>
                    <p className="font-medium text-slate-900 mb-3">"{example.idea}"</p>
                    <p className="text-sm text-slate-500 mb-1">Risultato:</p>
                    <p className="text-sm text-slate-600">{example.result}</p>
                  </div>
                </Card>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Button
                onClick={scrollToForm}
                className="bg-stone-800 hover:bg-stone-900 text-white px-8 py-3"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Inizia la Tua Richiesta
              </Button>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Cosa Dicono i Clienti
              </h2>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
              {TESTIMONIALS.map((testimonial, i) => (
                <Card key={i} className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-stone-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{testimonial.author}</p>
                      <p className="text-sm text-slate-500">{testimonial.location}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
                Domande Frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, i) => (
                  <Card
                    key={i}
                    className={`overflow-hidden cursor-pointer transition-all ${expandedFaq === i ? 'ring-2 ring-stone-400' : ''}`}
                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  >
                    <div className="p-5">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900">{faq.question}</h3>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === i ? 'rotate-90' : ''}`} />
                      </div>
                      {expandedFaq === i && (
                        <p className="mt-3 text-slate-600 text-sm">{faq.answer}</p>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 bg-gradient-to-r from-stone-800 to-amber-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Pronto a Commissionare la Tua Scultura?
            </h2>
            <p className="text-xl text-stone-200 mb-8 max-w-2xl mx-auto">
              Descrivi la tua visione e ricevi proposte da scultori italiani. È gratuito e senza impegno.
            </p>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-stone-800 hover:bg-stone-100 px-8 py-6 text-lg font-semibold shadow-xl"
            >
              <ArrowRight className="w-5 h-5 mr-2" />
              Inizia Ora
            </Button>
          </div>
        </section>

        {/* Auth Prompt Modal */}
        {showAuthPrompt && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                Accedi per Continuare
              </h3>
              <p className="text-slate-600 mb-6">
                Per inviare la tua richiesta di scultura, devi prima accedere o creare un account gratuito.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/accedi')}
                  className="w-full bg-stone-800 hover:bg-stone-900"
                >
                  Accedi
                </Button>
                <Button
                  onClick={() => navigate('/registrati')}
                  variant="outline"
                  className="w-full"
                >
                  Crea Account Gratuito
                </Button>
                <Button
                  onClick={() => setShowAuthPrompt(false)}
                  variant="ghost"
                  className="w-full"
                >
                  Annulla
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
